import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Alert,
  Typography, Box, Stack, useTheme, alpha, IconButton, CircularProgress,
} from '@mui/material';
import { Close, TrendingUp, CloudUpload } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import {
  RevenueCategory, RevenueSubCategory,
  REVENUE_METHODS,
  REVENUE_COUNTERPARTY_KINDS, REVENUE_COUNTERPARTY_KIND_LABELS,
} from '../../types/revenues';
import revenueCategoryService from '../../services/revenueCategoryService';
import PaymentMethodFields from '../common/PaymentMethodFields';
import { useTimezone } from '../../hooks/useTimezone';
import { formatFileSize } from '../../utils/fileSize';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (data: FormData) => Promise<void>;
}

const validationSchema = yup.object({
  title: yup.string()
    .required('Title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(200, 'Title must be at most 200 characters'),
  amount: yup.number()
    .typeError('Amount must be a number')
    .required('Amount is required')
    .moreThan(0, 'Amount must be greater than 0')
    .max(99999999, 'Amount is too large'),
  date: yup.string().required('Date is required'),
  categoryId: yup.string().required('Category is required'),
  method: yup.string().oneOf(REVENUE_METHODS).required('Method is required'),
  notes: yup.string().max(1000, 'Notes must be at most 1000 characters'),
  referenceNumber: yup.string().max(120, 'Reference is too long'),
  counterpartyName: yup.string().max(200, 'Name is too long'),
});

/**
 * Modal to record a new revenue entry. Mirror of CreateExpenseDialog with the
 * additions Revenue needs on the money-in side: method, reference number,
 * and an optional counterparty (who paid us). Category picker cascades into
 * sub-category on selection. Attachment is optional.
 */
const CreateRevenueDialog: React.FC<Props> = ({ open, onClose, onSuccess, onSubmit }) => {
  const theme = useTheme();
  const { currency } = useAuth();

  const [categories, setCategories] = useState<RevenueCategory[]>([]);
  const [subCategories, setSubCategories] = useState<RevenueSubCategory[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setCategoriesLoading(true);
      revenueCategoryService.getCategories()
        .then(setCategories)
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoading(false));
      setSubCategories([]);
      setFile(null);
    }
  }, [open]);

  // Income can't be dated in the future — the picker stops at today (school time).
  const { getToday } = useTimezone();
  const formik = useFormik({
    initialValues: {
      title: '',
      amount: '',
      date: getToday(),
      categoryId: '',
      subCategoryId: '',
      method: 'cash' as typeof REVENUE_METHODS[number],
      referenceNumber: '',
      counterpartyKind: '' as '' | typeof REVENUE_COUNTERPARTY_KINDS[number],
      counterpartyName: '',
      notes: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('title', values.title);
        formData.append('amount', String(values.amount));
        formData.append('date', values.date);
        formData.append('method', values.method);
        if (values.categoryId) formData.append('categoryId', values.categoryId);
        if (values.subCategoryId) formData.append('subCategoryId', values.subCategoryId);
        if (values.referenceNumber.trim()) formData.append('referenceNumber', values.referenceNumber.trim());
        if (values.counterpartyKind && values.counterpartyName.trim()) {
          formData.append('counterparty', JSON.stringify({
            kind: values.counterpartyKind,
            name: values.counterpartyName.trim(),
          }));
        }
        if (values.notes) formData.append('notes', values.notes);
        if (file) formData.append('revenueDocument', file);

        await onSubmit(formData);
        formik.resetForm();
        setFile(null);
        onSuccess();
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCategoryChange = async (categoryId: string) => {
    formik.setFieldValue('categoryId', categoryId);
    formik.setFieldValue('subCategoryId', '');
    if (categoryId) {
      try {
        const subs = await revenueCategoryService.getSubCategories(categoryId);
        setSubCategories(subs);
      } catch {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  };

  const handleClose = () => {
    if (loading) return;
    formik.resetForm();
    setFile(null);
    setSubCategories([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle component="div" sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.main,
              flexShrink: 0,
            }}
          >
            <TrendingUp />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>Record Revenue</Typography>
            <Typography variant="caption" color="text.secondary">
              Money received outside fees. It is recorded in the books straight away.
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 1.5 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                name="title"
                label="Title"
                placeholder="e.g. Hall hire — community event"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="amount"
                label={`Amount (${currency})`}
                type="number"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                name="date"
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: getToday() }}
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.date && Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                disabled={categoriesLoading}
              >
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formik.values.categoryId}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  onBlur={() => formik.setFieldTouched('categoryId', true)}
                  label="Category *"
                >
                  {categoriesLoading ? (
                    <MenuItem disabled>Loading…</MenuItem>
                  ) : categories.length === 0 ? (
                    <MenuItem disabled>No categories yet</MenuItem>
                  ) : categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formik.errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={!formik.values.categoryId || subCategories.length === 0}>
                <InputLabel>Sub-Category</InputLabel>
                <Select
                  value={formik.values.subCategoryId}
                  onChange={formik.handleChange}
                  name="subCategoryId"
                  label="Sub-Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {subCategories.map((sub) => (
                    <MenuItem key={sub._id} value={sub._id}>{sub.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <PaymentMethodFields formik={formik} />

            <Grid size={{ xs: 12, md: 5 }}>
              <FormControl fullWidth>
                <InputLabel>Received from</InputLabel>
                <Select
                  name="counterpartyKind"
                  value={formik.values.counterpartyKind}
                  onChange={formik.handleChange}
                  label="Received from"
                >
                  <MenuItem value="">(Not specified)</MenuItem>
                  {REVENUE_COUNTERPARTY_KINDS.map(k => (
                    <MenuItem key={k} value={k}>{REVENUE_COUNTERPARTY_KIND_LABELS[k]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <TextField
                fullWidth
                name="counterpartyName"
                label="Name (optional)"
                placeholder="e.g. Ali Khan, XYZ Grants Foundation"
                value={formik.values.counterpartyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.counterpartyName && Boolean(formik.errors.counterpartyName)}
                helperText={formik.touched.counterpartyName && formik.errors.counterpartyName}
                disabled={!formik.values.counterpartyKind}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                name="notes"
                label="Notes"
                multiline
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>

            <Grid size={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none', width: '100%', justifyContent: 'flex-start' }}
              >
                {file ? file.name : 'Attach receipt (image or PDF) — optional'}
                <input
                  hidden
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e: any) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              {file && (
                <Alert severity="info" icon={false} sx={{ mt: 1 }}>
                  Selected: {file.name} ({formatFileSize(file.size)})
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={loading || !formik.isValid || !formik.dirty}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <TrendingUp />}
            sx={{ textTransform: 'none', minWidth: 140 }}
          >
            {loading ? 'Recording…' : 'Record Revenue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateRevenueDialog;
